"use client";

import React from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import SharedContactForm from "@/components/Forms/SharedContactForm";
import { renderTextWithBreaks } from "@/utils/stripHTML";

interface ApptOption {
  label: string;
  value: string;
}

interface HeroSectionProps {
  selectedProduct: string;
  imageUrl?: string;
  title?: string;
  description?: string;
  formTitle?: string;
  formSubtitle?: string;
  formLayoutTypeTitle?: string;
  apptOptions?: ApptOption[];
  showForm?: boolean;
}

export default function HeroSection({
  selectedProduct,
  imageUrl,
  title = "KIẾN TẠO KHÔNG GIAN SỐNG HOÀN MỸ",
  description = "Tọa lạc tại vị trí vàng đắc địa, dự án mang đến trải nghiệm sống hoàn mỹ giữa thiên nhiên sinh thái trong lành cùng chuỗi tiện ích đẳng cấp, kiến tạo không gian sống xứng tầm vị thế chủ nhân.",
  formTitle = "ĐĂNG KÝ TƯ VẤN NGAY",
  formSubtitle = "Nhận thông tin mặt bằng & bảng giá mới nhất",
  formLayoutTypeTitle = "Sản phẩm quan tâm",
  apptOptions = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ],
  showForm = true,
}: HeroSectionProps) {
  return (
    <section
      id="banner"
      className="relative w-full min-h-screen bg-primary overflow-hidden flex items-center py-28 md:py-32"
    >
      <style>{`
        @keyframes scrollClouds {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll-clouds {
          animation: scrollClouds 60s linear infinite;
        }
        .radio-label {
          border: 1px solid rgba(0, 0, 0, 0.15);
          background-color: rgba(255, 255, 255, 0.3);
          color: #4b5563;
        }
        input[type="radio"]:checked + label {
          border-color: #B39775;
          background-color: #B39775;
          color: #ffffff;
          box-shadow: 0 4px 10px rgba(179, 151, 117, 0.2);
        }
        .transition-elegance {
          transition: all 0.5s ease;
        }
      `}</style>

      {/* BACKGROUND COVER IMAGE */}
      <div className="absolute inset-0 z-0">
        <SmoothImage
          src={imageUrl || "/images/hero_skyline.png"}
          alt="Skyline Park View"
          fill
          className="w-full h-full object-cover object-center"
          priority
        />
      </div>

      {/* TOP CLOUDS (ROTATED 180 DEG) */}
      <div className="absolute top-0 left-0 right-0 h-[120px] md:h-[200px] overflow-hidden pointer-events-none z-10 select-none rotate-180">
        <div className="flex w-[200%] h-full animate-scroll-clouds">
          <div
            className="w-1/2 h-full bg-no-repeat bg-bottom"
            style={{
              backgroundImage: "url('/images/cloud_deco.png')",
              backgroundSize: "100% 100%",
            }}
          />
          <div
            className="w-1/2 h-full bg-no-repeat bg-bottom"
            style={{
              backgroundImage: "url('/images/cloud_deco.png')",
              backgroundSize: "100% 100%",
              transform: "scaleX(-1)",
            }}
          />
        </div>
      </div>

      {/* BOTTOM CLOUDS */}
      <div className="absolute bottom-0 left-0 right-0 h-[180px] md:h-[280px] overflow-hidden pointer-events-none z-10 select-none">
        <div className="flex w-[200%] h-full animate-scroll-clouds">
          <div
            className="w-1/2 h-full bg-no-repeat bg-bottom"
            style={{
              backgroundImage: "url('/images/cloud_deco.png')",
              backgroundSize: "100% 100%",
            }}
          />
          <div
            className="w-1/2 h-full bg-no-repeat bg-bottom"
            style={{
              backgroundImage: "url('/images/cloud_deco.png')",
              backgroundSize: "100% 100%",
              transform: "scaleX(-1)",
            }}
          />
        </div>
      </div>

      {/* CONTENT GRID */}
      <div className="relative z-20 max-w-7xl mx-auto w-[90%] md:w-[86%] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center min-h-[70vh] lg:items-stretch">
        {/* Left Column: Headline and Subtitle (Aligned to bottom-left on desktop) */}
        <div
          className={`space-y-4 text-white text-left lg:self-end lg:pb-12 ${showForm ? "lg:col-span-7" : "lg:col-span-12"}`}
        >
          <div className="space-y-4 bg-black/40 p-6 md:p-8 backdrop-blur-[2px]">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black leading-tight sm:leading-[1.15] tracking-wide bg-gradient-to-r from-secondary via-secondary-light to-secondary bg-clip-text text-transparent uppercase font-montserrat">
              {renderTextWithBreaks(title)}
            </h1>
            <p className="text-[11px] sm:text-xs md:text-[13px] text-white/95 leading-relaxed font-semibold border-l-4 border-secondary pl-5 max-w-3xl">
              {renderTextWithBreaks(description)}
            </p>
          </div>
        </div>

        {/* Right Column: Floating Form (Remains centered/right aligned) */}
        {showForm && (
          <div className="lg:col-span-5 w-full flex justify-end lg:self-center">
            <div className="w-full max-w-[390px] bg-white/70 backdrop-blur-xl border border-white/60 p-6 md:p-8 rounded-none shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)] relative text-gray-800">
              {/* Top decorative line */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-[3px] bg-primary"></div>

              {/* Header */}
              <div className="text-center mb-5">
                <p className="text-primary text-[9px] font-bold uppercase tracking-[0.2em] mb-1.5">
                  Thông tin đặc quyền
                </p>
                <h3 className="text-lg md:text-xl font-bold text-primary-hover uppercase tracking-wide">
                  {formTitle}
                </h3>
                <p className="text-gray-500 text-[11px] mt-1 font-light italic">
                  {formSubtitle}
                </p>
              </div>

              <SharedContactForm
                theme="light"
                apptOptions={apptOptions}
                submitButtonText="Gửi thông tin"
                nguon={`Đăng ký từ Hero: sản phẩm: ${selectedProduct}`}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
